import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import { Btn, H3, MUIIcons, Progressbar } from "../../../AbstractElements";
import CommonModal from "../../../CommonElements/Modal";
import SearchBar from "../../../CommonElements/SearchBar";
import { useAppDispatch, useAppSelector } from "../../../ReduxToolkit/Hooks";
import {
  deleteUserGroup,
  getUserGroupList,
} from "../../../ReduxToolkit/Reducers/UserGroupSlice";
import {
  Action,
  Add,
  AddUser,
  AreYouSureDelete,
  Confirm,
  Confirmation,
  DeleteGroup,
  EditDetails,
  GroupName,
  NoOfLearningPlan,
  NoOfUser,
  Status,
  UserGroups,
} from "../../../utils/Constant";
import "./style.scss";

const Groups = () => {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [modalInfo, setModalInfo] = useState(null);
  const { userGroups } = useAppSelector(state => state.userGroup);
  const [data, setData] = useState([]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    getUserGroup();
  }, [dispatch]);

  const getUserGroup = async () => {
    await dispatch(getUserGroupList());
  };

  useEffect(() => {
    if (userGroups) {
      setData(userGroups?.user_groups);
    }
  }, [userGroups]);

  const toggleDropdown = (e, rowId) => {
    e.preventDefault();
    setDropdownOpen(prevDropdownOpen =>
      prevDropdownOpen === rowId ? null : rowId
    );
  };

  const groupListColumns = [
    {
      name: GroupName,
      selector: row => row?.name || "-",
      minWidth: "10rem",
      sortable: true,
    },
    {
      name: NoOfUser,
      selector: row => row?.no_of_users || "-",
      minWidth: "10rem",
      sortable: true,
    },
    {
      name: NoOfLearningPlan,
      selector: row => row?.no_of_learning_plan || "-",
      minWidth: "10rem",
      sortable: true,
    },
    {
      name: Status,
      minWidth: "15rem",
      sortable: true,
      cell: row => (
        <div className="group-list__status d-flex justify-content-between w-100 gap-2">
          <Progressbar className="w-100" color="dark" value={row?.progress} />
          <div>{`${row?.progress || 0}%`}</div>
        </div>
      ),
    },
    {
      name: Action,
      sortable: false,
      center: true,
      cell: row => (
        <div>
          <Dropdown
            isOpen={dropdownOpen === row.id}
            toggle={e => toggleDropdown(e, row.id)}
            direction="end"
          >
            <DropdownToggle color="" className="three-dots">
              <MUIIcons
                iconName="MoreVertOutlined"
                className="primary-icon-color"
                size={17}
              />
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem onClick={() => handleEditDetails(row)}>
                {EditDetails}
              </DropdownItem>
              <DropdownItem onClick={() => handleAddUser(row)}>
                {AddUser}
              </DropdownItem>
              <DropdownItem onClick={() => handleConfirmDelete(row)}>
                {DeleteGroup}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      ),
    },
  ];

  const handleConfirmDelete = row => {
    setModalInfo({
      isOpen: true,
      title: AreYouSureDelete,
      callback: () => handleDelete(row),
    });
  };

  const handleDelete = async row => {
    await dispatch(deleteUserGroup({ id: row?.id }));
    await getUserGroup();
    closeModal();
  };

  const handleClick = () => {
    return navigate("/user-management/add-new-groups");
  };

  const handleRowClick = row => {
    return navigate(`/user-management/group-details?group_id=${row?.id}`);
  };

  const handleAddUser = row => {
    return navigate(
      `/user-management/edit-new-groups?id=${row?.id}&type=user&page=home`
    );
  };

  const handleEditDetails = row => {
    return navigate(
      `/user-management/edit-new-groups?id=${row?.id}&type=basic`
    );
  };

  const closeModal = () => {
    setModalInfo(null);
  };

  const handleSearch = (query: string) => {
    const lowerQuery = query.trim().toLowerCase();

    if (!lowerQuery) {
      setData(userGroups?.user_groups || []);
      return;
    }

    const filteredGroups = userGroups?.user_groups?.filter(item =>
      item?.name?.toLowerCase()?.includes(lowerQuery)
    );

    setData(filteredGroups || []);
  };

  return (
    <div className="page-body page-body-margin group-list">
      <CommonModal
        sizeTitle={Confirmation}
        modalBodyClassName=""
        isOpen={modalInfo?.isOpen}
        toggle={closeModal}
        backdrop="static"
        size="lg"
        showFooter
        onPrimaryBtnClick={modalInfo?.callback}
        primaryBtnText={Confirm}
      >
        {modalInfo?.title}
      </CommonModal>
      <Card>
        <CardBody>
          <div className="d-md-flex flex-row align-items-center justify-content-between">
            <H3 className="fw-bold">{UserGroups}</H3>

            <div
              className={
                "d-flex flex-column flex-md-row justify-content-between"
              }
            >
              <div className="d-md-flex justify-content-end align-items-center mt-sm-0 gap-3 ms-md-auto">
                <SearchBar showSearch onSearch={handleSearch} />
                <Btn
                  icon={
                    <MUIIcons iconName="AddCircleOutlineOutlined" size={16} />
                  }
                  color="primary"
                  onClick={() => handleClick()}
                >
                  {Add}
                </Btn>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
      <DataTable
        className="group-list-table pointer"
        columns={groupListColumns}
        data={data}
        pagination
        fixedHeader
        striped
        onRowClicked={handleRowClick}
      />
    </div>
  );
};

export default Groups;
